import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { RequestService } from '../../services/request.service';
import { ValidationService } from '../../services/validation.service';
import { Subscription } from 'rxjs';
import panValidationMessageList from './_resources/pan-validation-message-list';
import { IValidationMessage } from '../../models/validation-message.interface';
import monthValidationMessageList from './_resources/month-validation-message-list';
import yearValidationMessageList from './_resources/year-validation-message-list';
import { ActivatedRoute, Router } from '@angular/router';
import { mergeMap } from 'rxjs/operators';
import { IRangeDictionaryItem } from '../../models/range-dictionary-item.interface';
import { IErrorState } from '../../models/error-state.interface';
import { Card3dsCheckDto } from '../../dto/card-3ds-check.dto';
import { Card3dsCheckResponseDto } from '../../dto/card-3ds-check-response.dto';
import { TppCommonService } from '@nspk/tpp-front-commons';

@Component({
    standalone: false,
    selector: 'app-card',
    templateUrl: './card.component.html',
    styleUrls: ['./card.component.scss']
})
export class CardComponent implements OnInit, OnDestroy {

    subscriptions: Subscription;

    form: UntypedFormGroup;

    loading: boolean;

    /**
     * Актуальные сообщения об ошибках
     */
    actualErrorMessages: { [key: string]: string };

    /**
     * Набор всех возможных ошибок валидации
     */
    validationErrorMessageLists: { [key: string]: IValidationMessage[] };

    rangeDictionary: IRangeDictionaryItem[];

    constructor(
        private requestService: RequestService,
        private tppCommonService: TppCommonService,
        private router: Router,
        private route: ActivatedRoute
    ) {
    }

    ngOnInit(): void {
        this.subscriptions = new Subscription();

        this.loading = true;

        this.subscriptions.add(this.route.queryParams
            .pipe(
                mergeMap(routeParams => {
                    const params = { token: routeParams.token || '' };
                    return this.requestService.get('/check', params);
                }),
                mergeMap(() => this.requestService.get<IRangeDictionaryItem[]>('/applicants/range', {}))
            )
            .subscribe(rangeDictionary => {
                this.rangeDictionary = rangeDictionary;
                this.loading = false;
            }));

        this.form = new UntypedFormGroup({
            pan: new UntypedFormControl(
                '',
                [Validators.required, ValidationService.luhnAlgorithm, this.cardRangeValidation.bind(this)]
            ),
            month: new UntypedFormControl('', [Validators.required, ValidationService.expireDateMonth]),
            year: new UntypedFormControl('', Validators.required)
        });

        this.actualErrorMessages = {};
        this.validationErrorMessageLists = {};

        Object.keys(this.form.controls).forEach(formControlName => {
            switch (formControlName) {
                case 'pan':
                    this.validationErrorMessageLists.pan = panValidationMessageList;
                    break;
                case 'month':
                    this.validationErrorMessageLists.month = monthValidationMessageList;
                    break;
                case 'year':
                    this.validationErrorMessageLists.year = yearValidationMessageList;
                    break;
            }

            this.setActualErrorMessage(formControlName);

            this.subscriptions.add(this.form.get(formControlName).valueChanges.subscribe(control => {
                this.setActualErrorMessage(formControlName);
            }));
        });
    }

    submitForm() {
        if (this.form.valid) {
            this.loading = true;

            const card3dsCheck: Card3dsCheckDto = {
                pan: this.form.get('pan').value,
                expDate: this.form.get('year').value + this.form.get('month').value,
                browserData: {
                    colorDepth: window.screen.colorDepth,
                    javaEnabled: window.navigator.javaEnabled(),
                    javascriptEnabled: true,
                    language: window.navigator.language,
                    screenHeight: window.screen.height,
                    screenWidth: window.screen.width,
                    timeZone: new Date().getTimezoneOffset(),
                    userAgent: window.navigator.userAgent
                }
            };

            this.subscriptions.add(
                this.requestService.post<Card3dsCheckResponseDto>('/applicants/verify-card', card3dsCheck)
                    .subscribe(res => {
                        if (res.verifyState.toLowerCase() === 'approved') {
                            this.router.navigate(['/card/pick']).then(() => {
                                this.loading = false;
                            });
                        } else {
                            if (res.cReq && res.acsUrl) {
                                const form = document.createElement('form');
                                form.setAttribute('method', 'POST');
                                form.setAttribute('enctype', 'application/x-www-form-urlencoded');
                                form.setAttribute('action', res.acsUrl);
                                form.style.display = 'none';

                                const input = document.createElement('input');
                                input.setAttribute('name', 'creq');
                                input.value = res.cReq;

                                form.append(input);

                                window.document.body.append(form);

                                form.submit();
                            } else {
                                this.tppCommonService.setCookie('gsRequestError', true);
                                this.router.navigate(['/error']);
                            }
                        }
                    }));
        } else {
            this.form.markAllAsTouched();
        }
    }

    ngOnDestroy(): void {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
            this.subscriptions = null;
        }
    }

    private cardRangeValidation(control: FormControl): IErrorState {
        return ValidationService.cardRangeValidation(control, this.rangeDictionary);
    }

    private setActualErrorMessage(formControlName: string) {
        const formControl = this.form.get(formControlName);
        const errorCode = formControl.errors ? Object.keys(formControl.errors)[0] : null;

        this.actualErrorMessages[formControlName] = (errorCode && formControl.invalid) ?
            ValidationService.getValidationMessage(errorCode, this.validationErrorMessageLists[formControlName]) : '';
    }
}
